// The agent builder's side of the page (RFC-060 model 2, model 10): the
// person's machine found from the main server, its workbench opened as the
// workbench page opens it, and the builder's calls (builder/*) and an agent's
// thread (ACP, a session with the agent as its persona) sent down it. What an
// agent is, is the machine's to say (byj agent build); what it is listed as
// while the machine is off, the main server's (GET /v1/agents).
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { api, ApiError, wsURL, type BuiltAgent, type MachineView } from "./api";
import { t } from "./i18n";
import type { SocketLike } from "./live";
import { Codes, emptyThread, reduceThread, RpcError, WorkbenchConnection, type PermissionItem, type WorkbenchState } from "./workbench";

/** An ability of the catalogue, as `byj agent build` says it, ticked or not. */
export interface AbilityRow {
  id: string;
  name: string;
  says: string;
  reach?: string;
  "ticked?": boolean;
  /** what it needs to connect, and whether it is set where a run reads it — never its value (byj-1wv.115.44) */
  needs?: { var: string; name: string; says: string; "set?": boolean }[];
}

/** A procedure: a skill of the agent's project the builder wrote, given the agent when ticked (byj-1wv.115.43). */
export interface ProcedureRow {
  name: string;
  description: string;
  content: string;
  "ticked?": boolean;
}

/** A procedure's steps: its SKILL.md without the frontmatter. */
export function procedureSteps(content: string): string {
  return content.replace(/^---\n[\s\S]*?\n---\n/, "").trim();
}

/** An example the agent is tested against (an RFC-034 task). */
export interface ExampleRow {
  id: string;
  prompt: string;
  verifiers?: { type: string; "contains-any"?: string[]; "contains-all"?: string[]; "not-contains"?: string[] }[];
  judge?: { rubric: string };
  /** the files it starts with, by path (byj-1wv.115.34) */
  files?: string[];
}

/** One example's outcome in a test. */
export interface ExampleResult {
  id: string;
  status: string;
  "passed?": boolean;
  checks: { name: string; "pass?": boolean; detail: string }[];
  answer?: string;
  error?: string;
}

export interface TestResult {
  at: number;
  passed: number;
  of: number;
  model?: string;
  examples: ExampleResult[];
}

/** What the builder reads of an agent on the machine (`byj agent build NAME --json`). */
export interface BuildInfo {
  name: string;
  cwd: string;
  spec: { description?: string; tools?: string[] };
  instruction?: string;
  abilities: AbilityRow[];
  procedures?: ProcedureRow[];
  examples: ExampleRow[];
  record: { "last-test"?: TestResult; proposed?: Record<string, string> };
  // services: the MCP servers it uses with the connection each was given;
  // service-writes: their tools that may change something there (byj-1wv.115.42)
  reach?: { level?: string; model?: string; "reads-anywhere"?: boolean; writes?: string[]; services?: string[]; "service-writes"?: string[] };
  // max_messages, max_cost: the caps it was last shared with, or byj-share's
  // defaults for one never shared (byj-1wv.115.48); absent from an older machine
  share: { share?: string; on?: boolean; max_messages?: number; max_cost?: number };
}

/** What the machine's Claude Code answers on (RFC-060 D15). */
export interface ModelState {
  provider: string;
  credential: "key" | "subscription" | "none";
  shares_with_others: boolean;
}

/** The person's machine running, with its share, or none; undefined while read. */
export function useMachine() {
  const [machine, setMachine] = useState<MachineView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const v = await api.machines();
      setMachine(v.machines.find((m) => m.state === "running" && m.share_id && !m.exam) ?? null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  return { machine, error, reload: load };
}

/**
 * How an agent stands, in a few words, as the main server keeps it: shared
 * and answering, shared with its machine off, or not shared — a share its
 * owner stopped among them, which `shared` tells from a machine off
 * (byj-1wv.115.49). /agents' rows and an agent's page with its machine off say it.
 */
export function agentStanding(a: BuiltAgent): string {
  switch (agentState(a)) {
    case "ended":
      return t("agents:notShared");
    case "live":
      return a.owner_only ? t("agents:sharedOwnerOnly") : t("agents:sharedLive");
    default:
      return t("agents:sharedOff");
  }
}

/**
 * How an agent stands, as a state bar and a row colour it: answering (live),
 * shared and not answering — its machine off, or reopening — (paused), or
 * not shared (ended). The one reckoning /agents and the page with its
 * machine off both read (byj-1wv.115.49).
 */
export function agentState(a: BuiltAgent): "live" | "paused" | "ended" {
  if (!a.share_id || !a.shared) return "ended";
  return a.live && a.connected ? "live" : "paused";
}

/**
 * An agent deleted (byj-1wv.115.49): on the machine first — its share
 * stopped, so it is never reopened, and its project removed
 * (`builder/delete`) — then forgotten by the main server, one it no longer
 * keeps counting as forgotten. Done only with the machine running:
 * forgotten alone, its project would reopen its share when the machine next
 * runs. One this machine does not have is forgotten at once if it is not
 * shared; shared, it may answer from the machine it was made on — or it was
 * deleted here already and not yet forgotten — so `forgetAnyway` asks first
 * rather than leave it never deletable. Says whether it was deleted.
 */
export async function deleteAgent(
  call: <T>(method: string, params?: unknown) => Promise<T>,
  name: string,
  row: BuiltAgent | undefined,
  forgetAnyway: () => Promise<boolean>,
): Promise<boolean> {
  let here = true;
  try {
    await call("builder/delete", { name });
  } catch (err) {
    if (!(err instanceof RpcError && err.code === Codes.notFound)) throw err;
    if (row?.shared && !(await forgetAnyway())) return false;
    here = false;
  }
  // deleted here, its share stopped with it: said so first, so a forget that
  // fails is tried again as one not shared, with nothing more to ask
  if (here && row?.shared) {
    try {
      await api.putAgent(name, { shared: false });
    } catch {
      // the forget below says what failed; tried again, it is asked about
    }
  }
  try {
    await api.deleteAgent(name);
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) throw err;
  }
  return true;
}

/** A thread's handlers: given what is for the session it owns. */
type ThreadSink = { update: (params: unknown) => void; permission: (rpc: string, params: unknown) => void };

/** At most this many of a session's messages are held before a thread claims it: a session never claimed holds no more. */
const heldPerSession = 500;

/**
 * The machine's workbench, opened on its share: `call` for the builder's
 * calls, answered once the sidecar is there; the state of the connection.
 * Session updates and permission requests go to the thread that owns their
 * session, several threads being on the page at once (the builder and the
 * agent tried, byj-1wv.115.48). One for a session no thread has claimed is
 * held while a thread's session/new is out — the session it is opening may
 * speak before the answer names it — and given to the thread that claims it;
 * with none out, or once the last is answered, what is still unclaimed is no
 * thread's (a session a page before a reload opened): its words are dropped,
 * and what it asks declined, so its turn ends rather than waits on no one.
 */
export function useBench(share: string | undefined, socket?: (url: string) => SocketLike) {
  const conn = useRef<WorkbenchConnection | null>(null);
  // each session's thread, what came for one not claimed yet (a permission
  // request with its rpc, to be declined if it never is), those let go, and
  // how many session/new are out
  const owners = useRef(new Map<string, ThreadSink>());
  const held = useRef(new Map<string, { rpc?: string; give: (s: ThreadSink) => void }[]>());
  const released = useRef(new Set<string>());
  const opening = useRef(0);
  const [state, setState] = useState<WorkbenchState>({ kind: "connecting" });
  const [sidecar, setSidecar] = useState(false);
  // counted at each "use it here": the workbench opened again, as the
  // workbench view takes it back from another tab (WorkbenchView's attempt)
  const [attempt, setAttempt] = useState(0);
  const decline = useCallback((rpc: string) => conn.current?.respond(rpc, { outcome: { outcome: "cancelled" } }), []);
  useEffect(() => {
    if (!share) return;
    // a connection opened again ("use it here") is not ready until its own sidecar says it is there
    setSidecar(false);
    const route =(params: unknown, give: (s: ThreadSink) => void, rpc?: string) => {
      const id = (params as { sessionId?: string } | null)?.sessionId;
      // ACP names the session of every update and permission asked: one that names none is no
      // thread's, and a permission asked for no thread is declined rather than shown in each
      if (id === undefined) {
        if (rpc) decline(rpc);
        return;
      }
      const owner = owners.current.get(id);
      if (owner) return give(owner);
      // let go, or no thread's with no session being opened: nowhere to go
      if (released.current.has(id) || opening.current === 0) {
        if (rpc) decline(rpc);
        return;
      }
      const q = held.current.get(id) ?? [];
      if (q.length < heldPerSession) q.push({ rpc, give });
      else if (rpc) decline(rpc);
      held.current.set(id, q);
    };
    const c = new WorkbenchConnection({
      url: wsURL(`/v1/shares/${encodeURIComponent(share)}/workbench`),
      socket,
      onState: setState,
      onNotification: (method, params) => {
        if (method === "workbench/sidecar") setSidecar((params as { connected?: boolean })?.connected === true);
        if (method === "session/update") route(params, (s) => s.update(params));
      },
      onRequest: (id, method, params) => {
        if (method === "session/request_permission") route(params, (s) => s.permission(id, params), id);
      },
    });
    conn.current = c;
    c.start();
    return () => {
      conn.current = null;
      held.current.clear();
      c.stop("left");
    };
  }, [share, socket, decline, attempt]);
  const call = useCallback(async <T,>(method: string, params?: unknown): Promise<T> => {
    const c = conn.current;
    if (!c) throw new RpcError(-32009, "the machine is not open");
    return c.request<T>(method, params);
  }, []);
  /** A thread's session/new about to go out: what comes for a session not claimed is held until it is answered. */
  const opens = useCallback(() => {
    opening.current += 1;
  }, []);
  /** A session/new answered: with none left out, what is still unclaimed is no thread's, and what it asked declined. */
  const opened = useCallback(() => {
    opening.current = Math.max(0, opening.current - 1);
    if (opening.current > 0) return;
    for (const q of held.current.values()) for (const h of q) if (h.rpc) decline(h.rpc);
    held.current.clear();
  }, [decline]);
  /** A session's updates are `sink`'s from now, with those that came before it was claimed. */
  const claim = useCallback((id: string, sink: ThreadSink) => {
    owners.current.set(id, sink);
    released.current.delete(id);
    const q = held.current.get(id);
    held.current.delete(id);
    q?.forEach((h) => h.give(sink));
  }, []);
  /** A session no thread shows any more: what comes for it is dropped, not held, and what it asked or asks declined. */
  const release = useCallback(
    (id: string) => {
      owners.current.delete(id);
      released.current.add(id);
      for (const h of held.current.get(id) ?? []) if (h.rpc) decline(h.rpc);
      held.current.delete(id);
    },
    [decline],
  );
  return {
    conn,
    opens,
    opened,
    claim,
    release,
    state,
    // why it was closed for good: another tab took it, it is not this person's, or the machine is gone
    ended: state.kind === "closed" ? state.end : undefined,
    // open, its machine not there yet
    waiting: state.kind === "open" && !sidecar,
    /** The workbench opened again here, taken back from another tab. */
    retake: () => setAttempt((n) => n + 1),
    ready: state.kind === "open" && sidecar,
    call,
  };
}

/**
 * A thread with an agent on the machine — the builder drafting, or the
 * agent made, tried — in its project: a session with the agent as its
 * persona (`_meta.brainyard.agent`), run by the workbench's byj. The session
 * outlives a dropped connection, as the sidecar keeps byj acp while the share
 * lives (RFC-057 D5), so a question cut off by the connection keeps it — its
 * turn may still finish there, and what it says comes once the page is back;
 * a question the machine refused lets it go, so the next opens a session
 * again — one byj acp no longer has (the machine started again) is asked no
 * more.
 */
export function useAgentThread(bench: ReturnType<typeof useBench>, cwd: string | undefined, persona: string) {
  const [thread, dispatch] = useReducer(reduceThread, emptyThread);
  const initialized = useRef(false);
  // its session, claimed from the bench as session/new names it: what is for
  // it comes here alone, another thread on the page having its own
  const session = useRef<string | undefined>(undefined);
  const sink = useRef<ThreadSink | null>(null);
  // counted each time its session is let go: a session/new answered after,
  // or a question's answer, is not this thread's any more, and not shown
  const era = useRef(0);
  const { opens, opened, claim, release } = bench;
  useEffect(() => {
    const s: ThreadSink = {
      update: (params) => dispatch({ type: "update", params }),
      permission: (rpc, params) => dispatch({ type: "permission", rpc, params }),
    };
    sink.current = s;
    if (session.current) claim(session.current, s);
    return () => {
      if (session.current) release(session.current);
      sink.current = null;
    };
  }, [claim, release]);
  /** Its session let go, to be opened again — initialized again too, a new byj acp perhaps — at the next question. */
  const drop = useCallback(() => {
    if (session.current) release(session.current);
    session.current = undefined;
    initialized.current = false;
    era.current += 1;
  }, [release]);
  /** Another thread from here on: its session let go, and what was said cleared. */
  const forget = useCallback(() => {
    drop();
    dispatch({ type: "reset" });
  }, [drop]);
  // another agent, or another project: another thread
  useEffect(() => {
    forget();
  }, [cwd, persona, forget]);
  const prompt = useCallback(
    (text: string) => {
      const c = bench.conn.current;
      if (!c || !cwd) return false;
      const mine = era.current;
      dispatch({ type: "prompt", text });
      (async () => {
        try {
          if (!initialized.current) {
            await c.request("initialize", { protocolVersion: 1, clientCapabilities: { fs: { readTextFile: false, writeTextFile: false }, terminal: false } });
            initialized.current = true;
          }
          let sid = session.current;
          if (!sid) {
            opens();
            let s: { sessionId: string };
            try {
              s = await c.request<{ sessionId: string }>("session/new", { cwd, mcpServers: [], _meta: { brainyard: { agent: persona } } });
              // let go while it was out: the session is no thread's, let go so nothing it says is kept
              if (mine !== era.current) {
                release(s.sessionId);
                return;
              }
              sid = s.sessionId;
              session.current = sid;
              dispatch({ type: "session", sessionId: sid });
              if (sink.current) claim(sid, sink.current);
            } finally {
              opened();
            }
          }
          const r = await c.request<{ stopReason?: string }>("session/prompt", { sessionId: sid, prompt: [{ type: "text", text }] });
          if (mine === era.current) dispatch({ type: "done", stopReason: r.stopReason });
        } catch (err) {
          if (mine !== era.current) return;
          dispatch({ type: "failed", message: err instanceof Error ? err.message : String(err) });
          // what was said stays; the next question opens a session again — unless only the connection
          // went, which the session outlives
          if (!(err instanceof RpcError && err.code === Codes.closed)) drop();
        }
      })();
      return true;
    },
    [bench.conn, cwd, persona, opens, opened, claim, release, drop],
  );
  const choose = useCallback(
    (p: PermissionItem, optionId: string) => {
      if (bench.conn.current?.respond(p.rpc, { outcome: { outcome: "selected", optionId } })) dispatch({ type: "chosen", rpc: p.rpc, optionId });
    },
    [bench.conn],
  );
  // reset: another thread; renew: the same thread, its next question on a session opened again (the agent changed)
  return { thread, prompt, choose, reset: forget, renew: drop };
}

/** A tool call's title in words a person reads (byj-1wv.115.26): the tool's name as what it did, and what it did it to. "" for a tool not named here, shown as its title. */
export function toolWords(title: string): { key: string; arg: string } {
  const [name = "", ...rest] = title.trim().split(/\s+/);
  const did: Record<string, string> = {
    "builder$knowledge": "readKnowledge",
    "meta-agent$create": "wroteInstruction",
    "meta-agent$validate": "checkedDraft",
    "meta-agent$read": "readDraft",
    "builder$propose": "proposed",
    "builder$example": "wroteExample",
    "read-file": "read",
    "list-dir": "looked",
    grep: "searched",
    search: "searched",
    "write-file": "wrote",
    "update-file": "wrote",
    bash: "ran",
  };
  return { key: did[name] ?? "", arg: rest.join(" ") };
}

/** The person's words in a thread, without what the page put before them for the builder agent. */
export function saidText(text: string): string {
  return text.replace(/^agent: [a-z][a-z0-9-]*\n(?:change: [^\n]*\n)?\n/, "");
}

/** The line that tells the builder agent a request is a change to an agent drafted, not a new one (its prompt's "A change to an agent already drafted"). */
export const changeLine = "change: the agent is drafted; read it and change only what is asked";

/** Whether an agent stopped because its model is not signed in on the machine (byj-1wv.115.27). */
export function notSignedIn(text: string): boolean {
  return /not logged in|please run \/login|invalid api key|authentication_error|oauth token has expired/i.test(text);
}

/** Whether an agent stopped before answering, by byj's own stop message, and why it says (byj-1wv.115.32). */
export function stoppedStuck(text: string): boolean {
  return /^Agent stopped:/.test(text.trim()) && !notSignedIn(text);
}

/** A name an agent may have: lowercase letters, digits and dashes. */
export const agentNameRe = /^[a-z][a-z0-9-]{0,62}$/;

/** The code an application calls a shared agent with, in each language the page shows. */
export function callCode(url: string): { ts: string; py: string; curl: string } {
  return {
    ts: `import { connectShared } from "@grumatic/byj-agent-sdk";

const agent = await connectShared("${url}", process.env.AGENT_TOKEN!);
const { answer } = await agent.ask("Can I still return a jacket bought 20 days ago?");`,
    py: `import asyncio, os
from byj_agent_sdk import connect_shared

async def main():
    agent = await connect_shared("${url}", os.environ["AGENT_TOKEN"])
    print((await agent.ask("Can I still return a jacket bought 20 days ago?")).answer)

asyncio.run(main())`,
    curl: `curl -s -X POST "${url}" \\
  -H "Authorization: Bearer $AGENT_TOKEN" -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"message/send","params":{"message":{"messageId":"m1","role":"user","parts":[{"kind":"text","text":"Can I still return a jacket bought 20 days ago?"}]}}}'`,
  };
}

/** An example's id from a question: kebab-case, a few words long. */
export function exampleId(question: string): string {
  const words = question
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 5);
  return words.length ? words.join("-") : "asked-by-a-caller";
}
